import React from 'react';
import { Mail, HelpCircle, MessageSquare, User, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const Support: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-mango-100 dark:bg-mango-900/20 rounded-full">
              <HelpCircle className="h-8 w-8 text-mango-600 dark:text-mango-400" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Support & Help
          </h1>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            We're here to help! Get in touch with any questions, feedback, or issues you might have.
          </p>
        </div>

        {/* Main Contact Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 mb-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-full">
                <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              Contact Support
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Please email us if you need any assistance with your account, the Cracked At platform, or have any feedback.
            </p>

            <div className="inline-flex items-center space-x-2 bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg">
              <Mail className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <a
                href="mailto:yiming.hong.mh@gmail.com"
                className="text-mango-600 dark:text-mango-400 font-medium hover:text-mango-700 dark:hover:text-mango-300 transition-colors"
              >
                yiming.hong.mh@gmail.com
              </a>
            </div>
          </div>
        </div>

        {/* Support Topics */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Account Issues */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <User className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Account Help</h3>
            </div>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li>• Subscription issues</li>
              <li>• Login problems</li>
              <li>• Account settings</li>
              <li>• Billing questions</li>
            </ul>
          </div>

          {/* Platform Issues */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Platform Issues</h3>
            </div>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li>• Technical problems</li>
              <li>• Question errors</li>
              <li>• Performance issues</li>
              <li>• Bug reports</li>
            </ul>
          </div>

          {/* Feedback */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <MessageSquare className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Feedback</h3>
            </div>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li>• Feature requests</li>
              <li>• Suggestions</li>
              <li>• General feedback</li>
              <li>• Testimonials</li>
            </ul>
          </div>
        </div>

        {/* Response Time */}
        <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-8">
          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
            Response Time
          </h3>
          <p className="text-blue-800 dark:text-blue-200 text-sm">
            We typically respond to support emails within 24-48 hours. For urgent issues, please mention "URGENT" in your subject line.
          </p>
        </div>

        {/* Back to Dashboard */}
        <div className="text-center">
          <Link
            to="/dashboard"
            className="inline-flex items-center px-4 py-2 bg-mango-600 text-white text-sm font-medium rounded-md hover:bg-mango-700 transition-colors"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Support;